import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTrophy, FaCalendarAlt, FaClock, FaChevronDown } from 'react-icons/fa';
import { fetchCalendrierEquipe } from '../../api/fff';
import useInView from '../../hooks/useInView';

// Mois de la saison (juillet à juin)
const MONTHS = [
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin'
];

// Retourne l'index du mois dans la saison (juillet=0, août=1, ..., juin=11)
function getMonthIndexInSeason(dateStr) {
  const date = new Date(dateStr);
  const month = date.getMonth(); // 0=janvier, 6=juillet
  return month >= 6 ? month - 6 : month + 6;
}

function getSeasonFromDate(dateStr) {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = date.getMonth();
  // Saison commence en juillet
  return month >= 6 ? `${year}/${year + 1}` : `${year - 1}/${year}`;
}

// Fonction pour déterminer le statut du match
function getMatchStatus(match) {
  const now = new Date();
  const matchDate = new Date(match.date);
  
  if (matchDate > now) {
    return { status: 'upcoming', label: 'À venir', color: 'text-[#BDFF00]', bg: 'bg-[#BDFF00]/20' };
  } else if (match.home_score !== null && match.away_score !== null) {
    return { status: 'finished', label: 'Terminé', color: 'text-green-400', bg: 'bg-green-400/20' };
  } else {
    return { status: 'live', label: 'En cours', color: 'text-[#FFE400]', bg: 'bg-[#FFE400]/20' };
  }
}

export default function EquipeResultats({ clubId, equipeId, nomEquipe }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [matches, setMatches] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [monthsInSeason, setMonthsInSeason] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [ref, inView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchCalendrierEquipe(clubId, equipeId)
      .then(({ all }) => {
        setMatches(all);
        // Extraire toutes les saisons présentes dans les matchs
        const saisonSet = new Set(all.map(m => getSeasonFromDate(m.date)));
        const saisonList = Array.from(saisonSet).sort().reverse();
        setSeasons(saisonList);
        setSelectedSeason(saisonList[0] || '');
        setLoading(false);
      })
      .catch(e => {
        setError('Erreur lors du chargement des matchs.');
        setLoading(false);
      });
  }, [clubId, equipeId]);

  // Met à jour la liste des mois disponibles pour la saison sélectionnée
  useEffect(() => {
    if (!selectedSeason || matches.length === 0) return;
    const moisSet = new Set(
      matches
        .filter(m => getSeasonFromDate(m.date) === selectedSeason)
        .map(m => getMonthIndexInSeason(m.date))
    );
    const moisList = Array.from(moisSet).sort((a, b) => a - b);
    setMonthsInSeason(moisList);
    // Par défaut, mois courant si dispo, sinon premier mois avec match
    const now = new Date();
    const currentMonthIdx = getMonthIndexInSeason(now);
    setSelectedMonth(
      moisList.includes(currentMonthIdx)
        ? currentMonthIdx
        : (moisList[0] ?? 0)
    );
  }, [selectedSeason, matches]);

  // Filtrage selon saison et mois
  const filteredMatches = matches.filter(m =>
    getSeasonFromDate(m.date) === selectedSeason &&
    getMonthIndexInSeason(m.date) === selectedMonth
  );

  // Prochain match (dans la saison sélectionnée)
  const now = new Date();
  const upcoming = matches.filter(m =>
    getSeasonFromDate(m.date) === selectedSeason && new Date(m.date) > now
  ).sort((a, b) => new Date(a.date) - new Date(b.date));
  const nextMatch = upcoming[0] || null;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8 }}
      className="w-full max-w-7xl mx-auto mt-12"
    >
      {/* Header avec glassmorphism */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-center mb-8"
      >
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-6 shadow-2xl mx-4">
          <div className="flex items-center justify-center gap-4 mb-2">
            <div className="bg-gradient-to-br from-[#FFE400] to-[#BDFF00] p-3 rounded-2xl shadow-lg">
              <FaTrophy className="text-[#001B56] text-2xl" />
            </div>
            <h2 className="text-4xl font-title font-extrabold bg-gradient-to-r from-[#FFE400] to-[#BDFF00] bg-clip-text text-transparent">
              RÉSULTATS & CALENDRIER
            </h2>
          </div>
          <p className="text-white/80 font-accent text-lg">
            Suivez tous les matchs de l'équipe
          </p>
        </div>
      </motion.div>

      {/* Prochain match en vedette */}
      {nextMatch && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-8 mx-4"
        >
          <div className="bg-gradient-to-br from-[#001B56] to-[#003399] rounded-3xl p-8 border border-[#FFE400]/30 shadow-2xl relative overflow-hidden">
            {/* Effets de fond */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#FFE400]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#BDFF00]/10 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <div className="flex items-center justify-center gap-3 mb-6">
                <FaCalendarAlt className="text-[#FFE400] text-xl" />
                <h3 className="text-2xl font-title font-bold text-[#FFE400]">
                  PROCHAIN MATCH
                </h3>
              </div>

              <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
                {/* Équipe domicile */}
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="flex flex-col items-center text-center"
                >
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 mb-3">
                    <img 
                      src={nextMatch.home.logo_path} 
                      alt="logo domicile" 
                      className="w-16 h-16 object-contain drop-shadow-lg" 
                    />
                  </div>
                  <span className="text-white font-accent font-bold text-lg">
                    {nextMatch.home.nom}
                  </span>
                </motion.div>

                {/* Score et infos */}
                <div className="flex flex-col items-center text-center">
                  <div className="bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] rounded-2xl px-8 py-4 shadow-lg mb-4">
                    <span className="text-3xl font-title font-extrabold">
                      {nextMatch.home_score ?? '-'} - {nextMatch.away_score ?? '-'}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-center">
                    <div className="flex items-center gap-2 text-[#BDFF00] font-accent">
                      <FaClock className="text-sm" />
                      <span>
                        {new Date(nextMatch.date).toLocaleString('fr-FR', { 
                          weekday: 'long', 
                          day: 'numeric', 
                          month: 'long', 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </span>
                    </div>
                    
                    {nextMatch.competition?.name && (
                      <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-3 py-1">
                        <span className="text-[#FFE400] font-accent font-bold text-sm">
                          {nextMatch.competition.name}
                        </span>
                      </div>
                    )}
                    
                    {nextMatch.journee && (
                      <span className="text-white/70 text-sm font-accent">
                        {nextMatch.journee}ème journée
                      </span>
                    )}
                  </div>
                </div>

                {/* Équipe extérieur */}
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="flex flex-col items-center text-center"
                >
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 mb-3">
                    <img 
                      src={nextMatch.away.logo_path} 
                      alt="logo extérieur" 
                      className="w-16 h-16 object-contain drop-shadow-lg" 
                    />
                  </div>
                  <span className="text-white font-accent font-bold text-lg">
                    {nextMatch.away.nom}
                  </span>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Sélecteurs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="flex flex-col sm:flex-row justify-center gap-4 mb-8 mx-4"
      >
        {/* Sélecteur de saison */}
        <div className="relative">
          <select
            className="appearance-none bg-white/10 backdrop-blur-md border border-white/20 text-white font-accent font-bold rounded-2xl px-6 py-3 pr-12 text-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-[#FFE400] focus:border-transparent transition-all duration-300"
            value={selectedSeason}
            onChange={e => setSelectedSeason(e.target.value)}
          >
            {seasons.map(s => (
              <option key={s} value={s} className="bg-[#001B56] text-white">
                Saison {s}
              </option>
            ))}
          </select>
          <FaChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-[#FFE400] pointer-events-none" />
        </div>

        {/* Menu horizontal des mois */}
        <div className="flex gap-2 flex-wrap justify-center">
          {monthsInSeason.map(idx => (
            <motion.button
              key={idx}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-4 py-2 rounded-xl font-accent font-bold text-sm transition-all duration-300 border ${
                selectedMonth === idx 
                  ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] border-[#FFE400] shadow-lg' 
                  : 'bg-white/10 backdrop-blur-md text-white border-white/20 hover:border-[#FFE400]/50'
              }`}
              onClick={() => setSelectedMonth(idx)}
            >
              {MONTHS[idx]}
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Liste des matchs */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 shadow-2xl mx-4"
      >
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-16"
            >
              {/* Spinner EFAFC */}
              <div className="relative mb-6">
                <div className="w-16 h-16 border-4 border-[#FFE400]/30 border-t-[#FFE400] rounded-full animate-spin"></div>
                <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-[#BDFF00] rounded-full animate-spin animate-reverse"></div>
              </div>
              <p className="text-[#FFE400] font-accent font-bold text-lg">
                Chargement des matchs...
              </p>
            </motion.div>
          ) : error ? (
            <motion.div
              key="error"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="text-center py-16"
            >
              <div className="bg-red-500/20 backdrop-blur-sm border border-red-500/30 rounded-2xl p-6 max-w-md mx-auto">
                <div className="text-red-400 text-4xl mb-4">⚠️</div>
                <h3 className="text-red-400 font-title font-bold text-xl mb-2">
                  Erreur de chargement
                </h3>
                <p className="text-red-300 font-accent">
                  {error}
                </p>
              </div>
            </motion.div>
          ) : filteredMatches.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="text-center py-16"
            >
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 max-w-md mx-auto">
                <div className="text-[#FFE400] text-5xl mb-4">📅</div>
                <h3 className="text-[#FFE400] font-title font-bold text-xl mb-2">
                  Aucun match
                </h3>
                <p className="text-white/70 font-accent">
                  Il n'y a pas de matchs à cette période.
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="matches"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              {filteredMatches.map((match, index) => {
                const matchStatus = getMatchStatus(match);
                
                return (
                  <motion.div
                    key={match['@id']}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden"
                  >
                    {/* Effet de fond */}
                    <div className="absolute top-0 right-0 w-20 h-20 bg-[#FFE400]/5 rounded-full blur-2xl" />
                    
                    <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      {/* Informations du match */}
                      <div className="flex items-center gap-6 flex-1">
                        {/* Équipe domicile */}
                        <div className="flex flex-col items-center text-center min-w-[80px]">
                          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-2 mb-2">
                            <img 
                              src={match.home.logo_path} 
                              alt="logo domicile" 
                              className="w-10 h-10 object-contain" 
                            />
                          </div>
                          <span className="text-white font-accent text-sm font-bold">
                            {match.home.nom}
                          </span>
                        </div>

                        {/* Score */}
                        <div className="bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] rounded-xl px-4 py-2 shadow-lg">
                          <span className="text-xl font-title font-extrabold">
                            {match.home_score ?? '-'} - {match.away_score ?? '-'}
                          </span>
                        </div>

                        {/* Équipe extérieur */}
                        <div className="flex flex-col items-center text-center min-w-[80px]">
                          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-2 mb-2">
                            <img 
                              src={match.away.logo_path} 
                              alt="logo extérieur" 
                              className="w-10 h-10 object-contain" 
                            />
                          </div>
                          <span className="text-white font-accent text-sm font-bold">
                            {match.away.nom}
                          </span>
                        </div>
                      </div>

                      {/* Détails du match */}
                      <div className="flex flex-col items-end text-right space-y-2 min-w-[200px]">
                        {/* Compétition */}
                        {match.competition?.name && (
                          <div className="bg-[#001B56]/50 backdrop-blur-sm border border-[#FFE400]/30 rounded-lg px-3 py-1">
                            <span className="text-[#FFE400] font-accent font-bold text-sm">
                              {match.competition.name}
                            </span>
                          </div>
                        )}
                        
                        {/* Date et heure */}
                        <div className="flex items-center gap-2 text-white/80 font-accent text-sm">
                          <FaClock className="text-xs" />
                          <span>
                            {new Date(match.date).toLocaleString('fr-FR', { 
                              weekday: 'short', 
                              day: 'numeric', 
                              month: 'short', 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </span>
                        </div>
                        
                        {/* Journée */}
                        {match.journee && (
                          <span className="text-[#BDFF00] text-sm font-accent">
                            {match.journee}ème journée
                          </span>
                        )}
                        
                        {/* Statut */}
                        <div className={`${matchStatus.bg} backdrop-blur-sm border border-white/20 rounded-lg px-3 py-1`}>
                          <span className={`${matchStatus.color} text-sm font-accent font-bold`}>
                            {matchStatus.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Effets de fond animés */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-1/4 left-1/4 w-32 h-32 bg-[#FFE400]/5 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            x: [0, -80, 0],
            y: [0, 60, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-[#BDFF00]/5 rounded-full blur-3xl"
        />
      </div>
    </motion.div>
  );
} 